import { inject, Injectable } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	RedirectCommand,
	Resolve,
	Router,
} from '@angular/router';
import { AlertService } from '@marmon/frontend/alert';
import { isPast, USER } from '@marmon/frontend/common';
import { AlertId } from '@marmon/shared/types/alert';
import { Analysis, AnalysisCalculation } from '@marmon/shared/types/analysis';
import { isAdmin } from '@marmon/shared/types/user';
import { Timestamp } from 'firebase/firestore';
import { AnalysisApiService, AnalysisStateService } from '../services';

@Injectable({ providedIn: 'root' })
export class AnalysisDetailResolve implements Resolve<Analysis> {
	readonly analysisApiService = inject(AnalysisApiService);
	readonly analysisStateService = inject(AnalysisStateService);

	readonly user$ = inject(USER);

	readonly router = inject(Router);
	readonly alert = inject(AlertService);

	async resolve(
		route: ActivatedRouteSnapshot,
	): Promise<Analysis | RedirectCommand> {
		const analysisId =
			route.paramMap.get('analysisId') ??
			route.parent?.paramMap.get('analysisId');
		if (!analysisId) return this.rerouteHome('generic-error');

		const user = this.user$.value;
		if (!user) return this.rerouteHome('generic-error');

		const newAnalysis =
			this.analysisStateService.analysis?.id !== analysisId;
		const analysis = await this.analysisApiService
			.findById(analysisId)
			.catch((error) => {
				console.error('AnalysisDetailResolve.resolve', error);
				return null;
			});

		// Not found
		if (!analysis) return this.rerouteHome('analysis-not-found-error');

		const options = {
			requireAccess: !isAdmin(user),
			...(route.data ?? {}),
		};

		if (
			options.requireAccess &&
			!analysis.shared.find((s) => s.id === user.id)
		) {
			return this.rerouteHome('analysis-access-denied');
		}

		if (isPast(analysis.deletedAt as Timestamp)) {
			// Deleted
			return this.rerouteHome('analysis-deleted-error');
		}

		// Expired
		if (isPast(analysis.expiresAt as Timestamp)) {
			return this.rerouteHome('analysis-expired-error');
		}

		let calculation: AnalysisCalculation | null = null;
		if (
			// if the analysis being loaded doesn't equal to new analysis
			newAnalysis ||
			// if no calculation is currently loaded
			!this.analysisStateService.calculation
		) {
			calculation = await this.analysisApiService
				.fetchCalculation({
					id: route.queryParamMap.get('calculation') ?? undefined,
					analysis,
				})
				.catch((error) => {
					// we can bypass this as a real issue and offload to the stream after load
					console.error('The calculation could not be loaded', error);
					return null;
				});
		}

		this.analysisStateService.update(analysis, calculation ?? undefined);
		return analysis;
	}

	protected rerouteHome(alert: AlertId) {
		this.analysisStateService.update(null);
		this.alert.open(alert).subscribe();
		return new RedirectCommand(this.router.parseUrl('/home'));
	}
}
